/**
 * Civil dates — year, month and day, with no time and no timezone.
 *
 * A birth date and an event date are positions on the calendar, not instants, so nothing here
 * builds a `Date` from text: `new Date("1995-06-14")` is midnight **UTC** and prints a day
 * earlier for anyone west of it. The functions take components, and validation is arithmetic.
 */

export type CivilDate = {
  year: number;
  /** 1 to 12 — **not** a `Date` month index. */
  month: number;
  day: number;
};

const ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
const BR_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/u;

export function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/** How many days the month has. Returns 0 for a month outside 1..12. */
export function daysInMonth(year: number, month: number): number {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  return days[month - 1] ?? 0;
}

/** Does the date exist on the calendar? 31/02 and 29/02 of a common year do not. */
export function isValidCivilDateParts(parts: CivilDate): boolean {
  const { year, month, day } = parts;

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;

  return year >= 1 && month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/** `yyyy-mm-dd` to components. Returns `null` when the text is not a date. */
export function parseIsoCivilDate(input: string): CivilDate | null {
  const match = ISO_PATTERN.exec(input);
  if (match === null) return null;

  const parts = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };

  return isValidCivilDateParts(parts) ? parts : null;
}

/** `dd/mm/yyyy` to components — the format a person reads and writes. */
export function parseBrCivilDate(input: string): CivilDate | null {
  const match = BR_PATTERN.exec(input);
  if (match === null) return null;

  const parts = { year: Number(match[3]), month: Number(match[2]), day: Number(match[1]) };

  return isValidCivilDateParts(parts) ? parts : null;
}

/** Accepts both formats, so the caller does not have to know which one arrived. */
export function parseCivilDate(input: unknown): CivilDate | null {
  if (typeof input !== "string") return null;

  return parseIsoCivilDate(input) ?? parseBrCivilDate(input);
}

export function formatIsoCivilDate({ year, month, day }: CivilDate): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function formatBrCivilDate({ year, month, day }: CivilDate): string {
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year).padStart(4, "0")}`;
}

export function isValidCivilDate(input: string): boolean {
  return parseIsoCivilDate(input) !== null;
}

/** Negative when `a` comes first, zero for the same date, positive when it comes after. */
export function compareCivilDate(a: CivilDate, b: CivilDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/**
 * Completed years at a reference date. Component arithmetic: the birthday counts from the day,
 * with no dependence on milliseconds per year.
 */
export function completedYearsBetween(birth: CivilDate, reference: CivilDate): number {
  const years = reference.year - birth.year;
  const beforeBirthday =
    reference.month < birth.month ||
    (reference.month === birth.month && reference.day < birth.day);

  return beforeBirthday ? years - 1 : years;
}

/**
 * Bridge to the APIs that only speak `Date`.
 *
 * Builds from **local components**, never from text: `new Date(1995, 5, 14)` is local midnight,
 * and that is the date the person sees.
 */
export function civilDateToLocalDate({ year, month, day }: CivilDate): Date {
  return new Date(year, month - 1, day);
}

/** The way back, reading the `Date`'s local components. */
export function localDateToCivilDate(date: Date): CivilDate {
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

/**
 * Adds days, crossing month and year.
 *
 * Uses the **component** `Date` constructor, which already knows how to overflow (31 January
 * plus a day is 1 February). This is the bridge sanctioned above: what the project forbids is
 * building a date from text, not using the calendar arithmetic the platform gets right.
 */
export function addCivilDays(date: CivilDate, days: number): CivilDate {
  return localDateToCivilDate(new Date(date.year, date.month - 1, date.day + days));
}

/**
 * Adds months **clamping the day to the end of the target month**.
 *
 * Without the clamp, 31 January plus one month would overflow to 3 March — the wrong month on
 * a calendar. Paging forward should land in February, on the last day February has.
 */
export function addCivilMonths(date: CivilDate, months: number): CivilDate {
  const monthIndex = date.month - 1 + months;
  const year = date.year + Math.floor(monthIndex / 12);
  const month = ((monthIndex % 12) + 12) % 12 + 1;

  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

/** First and last day of the date's month — the Home and End targets. */
export function startOfCivilMonth({ year, month }: CivilDate): CivilDate {
  return { year, month, day: 1 };
}

export function endOfCivilMonth({ year, month }: CivilDate): CivilDate {
  return { year, month, day: daysInMonth(year, month) };
}

/** Month names as the design writes them, lower case in the middle of the sentence. */
const MONTHS_IN_FULL = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/**
 * A date in words, the way the design writes the event's: "12 de dezembro de 2026".
 *
 * The backend keeps `DD/MM/YYYY` and the form takes it in that shape, because that is what it
 * accepts (`IsBrDate`); only the reading is in words. Anything that is not a `DD/MM/YYYY` date —
 * an empty field, or a date already written in words — is handed back untouched, so nothing is
 * ever rewritten into something it is not.
 *
 * Built from the parts, never from `new Date`: a date parsed as UTC and read in Brazil is the
 * day before.
 */
export function formatCivilDateInFull(value: string): string {
  const parts = parseBrCivilDate(value);
  if (parts === null) return value;

  return `${String(parts.day).padStart(2, "0")} de ${MONTHS_IN_FULL[parts.month - 1]} de ${parts.year}`;
}
